// PurchaseOrderSignatures.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

// Context
import { useBase } from 'context/BaseContext';

// Hooks
import useSubscribedNode from 'hooks/useSubscribedNode';

// Services
import { getPurchaseOrderSettings } from 'services/bases/purchaseOrderBaseSettings';
import {
  createPoSignInvite,
  getPoSignInvite,
  revokePoSignInvite,
  sendPoSignInviteEmail,
} from 'services/purchaseOrders/PoSignInviteService';

// Utilities
import { buildNodeFieldPatch } from 'utilities/NodeFieldData';
import { isSignatureFieldSigned } from 'utilities/SignatureFieldValue';
import { readPurchaseOrderField } from 'nodetypes/purchaseorder/view/purchaseOrderGeneralInfoModel';
import {
  getPurchaseOrderSchemaField,
  getPurchaseOrderVendorKey,
  getVendorPartyDisplay,
} from 'nodetypes/purchaseorder/view/purchaseOrderPartyUtils';

// CSS
import './PurchaseOrderSignatures.css';

// Components
import Button from 'components/button/Button';
import NodeSignatureField from 'node/form/fields/signature/NodeSignatureField';

const COMPANY_FIELD_NAME = 'companySignature';
const VENDOR_FIELD_NAME = 'vendorSignature';
const DEFAULT_LINK_EXPIRATION_KEY = '30d';
const SHARE_URL_SESSION_PREFIX = 'appdeck.poSignShareUrl.';

function resolveSignatureField(nodeType, fieldName, fallbackLabel) {
  const schemaField = getPurchaseOrderSchemaField(nodeType, fieldName);
  if (schemaField) return schemaField;
  return {
    name: fieldName,
    label: fallbackLabel,
    type: 'signature',
    source: 'purchase_order',
  };
}

function shareUrlSessionKey(poKey) {
  return `${SHARE_URL_SESSION_PREFIX}${String(poKey || '').trim()}`;
}

function readSessionShareUrl(poKey) {
  if (typeof sessionStorage === 'undefined') return '';
  try {
    return String(sessionStorage.getItem(shareUrlSessionKey(poKey)) || '').trim();
  } catch {
    return '';
  }
}

function writeSessionShareUrl(poKey, url) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    const key = shareUrlSessionKey(poKey);
    const cleaned = String(url || '').trim();
    if (!cleaned) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, cleaned);
  } catch {
    // ignore quota / private mode
  }
}

function formatInviteExpiry(invite) {
  if (!invite || invite.status !== 'pending') return '';
  if (!invite.expiresAtMillis) return 'Expires in 30 days';
  const when = new Date(invite.expiresAtMillis).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return `Expires ${when}`;
}

/**
 * PurchaseOrderSignatures
 *
 * Houzz-inspired two-party Signature block for the PURCHASE_ORDER viewer.
 */
const PurchaseOrderSignatures = React.memo(({
  node,
  nodeType = null,
  readOnly = false,
  onNodeChange = null,
  /**
   * Portal session: vendor can sign; company link UI hidden.
   * @type {null|{
   *   vendorEditable?: boolean,
   *   settings?: object,
   *   vendorPartyName?: string,
   *   signatureImageUrls?: { companySignature?: string, vendorSignature?: string },
   *   onVendorSignatureChange?: (nextValue: object|null) => void|Promise<void>,
   *   onVendorSignWritten?: (args: { name: string, dataUrl: string }) => Promise<object|null>,
   * }}
   */
  portal = null,
}) => {
  const { activeBase } = useBase();
  const isPortal = Boolean(portal);
  const canEdit = Boolean(!readOnly && typeof onNodeChange === 'function' && node?.key);
  const typeKey = String(nodeType?.key || node?.type || 'PURCHASE_ORDER').trim().toUpperCase();

  const settings = useMemo(() => {
    if (portal?.settings) {
      return {
        ...getPurchaseOrderSettings(null),
        ...portal.settings,
      };
    }
    return getPurchaseOrderSettings(activeBase);
  }, [activeBase, portal?.settings]);

  const companyTitle = settings.companySignatureTitle;
  const vendorTitle = settings.vendorSignatureTitle;
  const subtitle = settings.signatureSubtitle;
  const showAs = settings.signatureShowAs;

  const vendorKey = useMemo(
    () => getPurchaseOrderVendorKey(node, nodeType),
    [node, nodeType],
  );
  const vendorNode = useSubscribedNode(vendorKey, {
    enabled: Boolean(vendorKey) && !isPortal,
  });
  const vendorDisplay = useMemo(
    () => getVendorPartyDisplay(vendorNode),
    [vendorNode],
  );
  const vendorPartyName = String(
    portal?.vendorPartyName
    || vendorDisplay?.title
    || '',
  ).trim();

  const companyField = useMemo(
    () => resolveSignatureField(nodeType, COMPANY_FIELD_NAME, companyTitle),
    [companyTitle, nodeType],
  );
  const vendorField = useMemo(
    () => resolveSignatureField(nodeType, VENDOR_FIELD_NAME, vendorTitle),
    [nodeType, vendorTitle],
  );

  const companyValue = useMemo(
    () => readPurchaseOrderField(node, COMPANY_FIELD_NAME, nodeType),
    [node, nodeType],
  );
  const vendorValue = useMemo(
    () => readPurchaseOrderField(node, VENDOR_FIELD_NAME, nodeType),
    [node, nodeType],
  );

  const companySigned = isSignatureFieldSigned(companyValue);
  const canEditCompany = Boolean(canEdit && !isPortal);
  const canEditVendor = Boolean(
    isPortal
      ? portal?.vendorEditable !== false
      : false,
  );

  const baseKey = String(activeBase?.key || node?.baseKey || '').trim();
  const poKey = String(node?.key || '').trim();
  const canManageInvite = Boolean(!isPortal && canEdit && baseKey && poKey);

  const [invite, setInvite] = useState(null);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [lastShareUrl, setLastShareUrl] = useState('');
  const [inviteLoaded, setInviteLoaded] = useState(false);
  const companyWasSignedRef = useRef(false);
  const mintInFlightRef = useRef(false);

  useEffect(() => {
    companyWasSignedRef.current = companySigned;
  }, [companySigned, poKey]);

  useEffect(() => {
    if (!canManageInvite) {
      setInvite(null);
      setLastShareUrl('');
      setInviteLoaded(false);
      return undefined;
    }

    let cancelled = false;
    setInviteLoaded(false);
    setLastShareUrl(readSessionShareUrl(poKey));

    (async () => {
      try {
        const next = await getPoSignInvite({ baseKey, poKey });
        if (!cancelled) setInvite(next);
      } catch (error) {
        if (!cancelled) {
          console.warn('[PurchaseOrderSignatures] getPoSignInvite failed', error);
          setInvite(null);
        }
      } finally {
        if (!cancelled) setInviteLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [baseKey, canManageInvite, poKey]);

  const mintShareLink = useCallback(async ({ quiet = false } = {}) => {
    if (!canManageInvite || mintInFlightRef.current) return null;
    mintInFlightRef.current = true;
    setInviteBusy(true);
    try {
      const created = await createPoSignInvite({
        baseKey,
        poKey,
        expirationKey: DEFAULT_LINK_EXPIRATION_KEY,
      });
      setInvite(created);
      const url = String(created?.shareUrl || '').trim();
      setLastShareUrl(url);
      writeSessionShareUrl(poKey, url);
      if (!quiet) {
        toast.success('Vendor sign link ready.', { autoClose: 2500 });
      }
      return url;
    } catch (error) {
      if (!quiet) {
        toast.error(error?.message || 'Could not create sign link.', { autoClose: 4000 });
      } else {
        console.warn('[PurchaseOrderSignatures] auto mint failed', error);
      }
      return null;
    } finally {
      mintInFlightRef.current = false;
      setInviteBusy(false);
    }
  }, [baseKey, canManageInvite, poKey]);

  const handlePartyChange = useCallback((field, nextValue) => {
    if (!node?.key || !field) return;
    if (isPortal && field.name === VENDOR_FIELD_NAME) {
      if (typeof portal?.onVendorSignatureChange === 'function') {
        portal.onVendorSignatureChange(nextValue);
      }
      return;
    }
    const patch = buildNodeFieldPatch(node, field, typeKey, nextValue);
    if (!patch) return;
    onNodeChange(node.key, patch);

    if (
      field.name === COMPANY_FIELD_NAME
      && canManageInvite
      && !companyWasSignedRef.current
      && isSignatureFieldSigned(nextValue)
    ) {
      companyWasSignedRef.current = true;
      mintShareLink({ quiet: true }).then((url) => {
        if (url) {
          toast.success('Vendor sign link ready to copy.', { autoClose: 2500 });
        }
      });
    }

    if (field.name === COMPANY_FIELD_NAME && !isSignatureFieldSigned(nextValue)) {
      companyWasSignedRef.current = false;
    }
  }, [canManageInvite, isPortal, mintShareLink, node, onNodeChange, portal, typeKey]);

  const handleCopyLink = useCallback(async () => {
    const url = String(lastShareUrl || '').trim();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied.', { autoClose: 2000 });
    } catch {
      toast.error('Could not copy the link.', { autoClose: 3000 });
    }
  }, [lastShareUrl]);

  const handleNewLink = useCallback(() => {
    mintShareLink({ quiet: false });
  }, [mintShareLink]);

  const handleRevokeLink = useCallback(async () => {
    if (!canManageInvite || inviteBusy) return;
    setInviteBusy(true);
    try {
      await revokePoSignInvite({ baseKey, poKey });
      setInvite((prev) => (prev ? { ...prev, status: 'revoked', shareUrl: null, token: null } : null));
      setLastShareUrl('');
      writeSessionShareUrl(poKey, '');
      toast.success('Sign link revoked.', { autoClose: 2500 });
    } catch (error) {
      toast.error(error?.message || 'Could not revoke sign link.', { autoClose: 4000 });
    } finally {
      setInviteBusy(false);
    }
  }, [baseKey, canManageInvite, inviteBusy, poKey]);

  const handleEmailVendor = useCallback(async () => {
    if (!canManageInvite || inviteBusy || !companySigned) return;
    setInviteBusy(true);
    try {
      const result = await sendPoSignInviteEmail({
        baseKey,
        poKey,
        expirationKey: DEFAULT_LINK_EXPIRATION_KEY,
      });
      const invitePayload = result?.invite || null;
      if (invitePayload) setInvite(invitePayload);
      const url = String(invitePayload?.shareUrl || result?.invite?.shareUrl || '').trim();
      if (url) {
        setLastShareUrl(url);
        writeSessionShareUrl(poKey, url);
      }

      if (result?.redirected) {
        toast.success(
          `Sign link emailed to ${result.deliveredTo} (dev redirect; intended ${result.intendedTo}).`,
          { autoClose: 4500 },
        );
      } else {
        toast.success(
          `Sign link emailed to ${result?.intendedTo || 'vendor'}.`,
          { autoClose: 3000 },
        );
      }
    } catch (error) {
      const message = String(
        error?.details?.message
        || error?.message
        || 'Could not email the vendor.',
      ).replace(/^Firebase:\s*/i, '').replace(/\s*\(functions\/[^)]+\)\s*$/i, '');
      toast.error(message || 'Could not email the vendor.', { autoClose: 5000 });
    } finally {
      setInviteBusy(false);
    }
  }, [baseKey, canManageInvite, companySigned, inviteBusy, poKey]);

  if (!node) return null;

  const inviteIsActive = invite?.status === 'pending';
  const showInviteChrome = Boolean(
    canManageInvite
    && inviteLoaded
    && (companySigned || inviteIsActive || lastShareUrl),
  );
  const expiryLabel = formatInviteExpiry(invite);

  return (
    <div className="purchase-order-signatures">
      <div className="purchase-order-signatures-header">
        <div className="purchase-order-signatures-title">
          Signature
        </div>
      </div>

      {subtitle ? (
        <div className="purchase-order-signatures-subtitle">
          {subtitle}
        </div>
      ) : null}

      <div className="purchase-order-signatures-body">
        <div className="purchase-order-signatures-grid">
          <div className="purchase-order-signatures-party">
            <NodeSignatureField
              node={node}
              field={companyField}
              fieldLabel={companyTitle}
              value={companyValue}
              showAs={showAs}
              readOnly={!canEditCompany}
              resolvedImageUrl={portal?.signatureImageUrls?.companySignature || null}
              onChange={(nextValue) => handlePartyChange(companyField, nextValue)}
            />
            <div className="purchase-order-signatures-party-label">
              {companyTitle}
            </div>
          </div>

          <div className="purchase-order-signatures-party">
            <NodeSignatureField
              node={node}
              field={vendorField}
              fieldLabel={vendorTitle}
              value={vendorValue}
              showAs={showAs}
              defaultName=""
              readOnly={!canEditVendor}
              resolvedImageUrl={portal?.signatureImageUrls?.vendorSignature || null}
              onSignWritten={
                isPortal && typeof portal?.onVendorSignWritten === 'function'
                  ? portal.onVendorSignWritten
                  : null
              }
              onChange={(nextValue) => handlePartyChange(vendorField, nextValue)}
            />
            <div className="purchase-order-signatures-party-label">
              {vendorTitle}
            </div>
            {vendorPartyName ? (
              <div className="purchase-order-signatures-party-context">
                {vendorPartyName}
              </div>
            ) : null}
          </div>
        </div>

        {showInviteChrome ? (
          <div className="purchase-order-signatures-invite">
            <div className="purchase-order-signatures-invite-label">
              Vendor sign link
            </div>

            {lastShareUrl ? (
              <div className="purchase-order-signatures-invite-row">
                <div className="purchase-order-signatures-invite-url" title={lastShareUrl}>
                  {lastShareUrl}
                </div>
                <Button
                  label="Copy"
                  height="32px"
                  enabled={!inviteBusy}
                  onClick={handleCopyLink}
                />
                <Button
                  label={inviteBusy ? 'Sending…' : 'Email vendor'}
                  height="32px"
                  enabled={!inviteBusy && companySigned}
                  onClick={handleEmailVendor}
                />
              </div>
            ) : inviteIsActive ? (
              <div className="purchase-order-signatures-invite-hint">
                A vendor link is already active. Create a new one to copy it.
              </div>
            ) : inviteBusy ? (
              <div className="purchase-order-signatures-invite-hint">
                Creating vendor sign link…
              </div>
            ) : null}

            {expiryLabel ? (
              <div className="purchase-order-signatures-invite-status">
                {expiryLabel}
              </div>
            ) : null}

            <div className="purchase-order-signatures-invite-actions">
              {!lastShareUrl ? (
                <>
                  <Button
                    label={inviteBusy ? 'Working…' : 'New link'}
                    height="32px"
                    enabled={!inviteBusy && companySigned}
                    onClick={handleNewLink}
                  />
                  <Button
                    label={inviteBusy ? 'Sending…' : 'Email vendor'}
                    height="32px"
                    enabled={!inviteBusy && companySigned}
                    onClick={handleEmailVendor}
                  />
                </>
              ) : (
                <Button
                  label={inviteBusy ? 'Working…' : 'New link'}
                  height="32px"
                  backgroundColor="transparent"
                  enabled={!inviteBusy}
                  onClick={handleNewLink}
                />
              )}
              <Button
                label="Revoke"
                height="32px"
                backgroundColor="transparent"
                enabled={!inviteBusy && inviteIsActive}
                onClick={handleRevokeLink}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
});

PurchaseOrderSignatures.displayName = 'PurchaseOrderSignatures';

export default PurchaseOrderSignatures;
